import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { GeneralService } from 'src/app/services/general/general.service';
import { CarService } from 'src/app/services/car/car.service';
import { PaginationInfo, PaginatedResponse } from 'src/app/shared/models';
import { BidSavedSearchService } from 'src/app/services/bid-saved-search/bid-saved-search.service';
import { SavedSearchModalService } from 'src/app/services/saved-search-modal/saved-search-modal.service';
import swal from 'sweetalert2';
import { TransmissionService } from 'src/app/services/transmission/transmission.service';
import { BodyColorService } from 'src/app/services/body-color/body-color.service';
import { VehicleService } from 'src/app/services/vehicle/vehicle.service';

declare var bootstrap: any;
declare var Swal: any;

interface FilterOption {
  value: string;
  label: string;
  checked?: boolean;
}

interface SearchFilters {
  dates: string[];
  auctions: string[];
  makes: string[];
  models: string[];
  chassisModels: string[];
  lotNumber: string;
  lotSearchAuction: string;
  // More Filters dropdowns
  yearFrom: string;
  yearTo: string;
  mileageFrom: string;
  mileageTo: string;
  transmission: string;
  color: string;
  displacement: string;
  grade: string;
}

interface CachedMakeData {
  name: string;
  slug: string;
  count: number;
  models: { [modelSlug: string]: CachedModelData };
}

interface CachedModelData {
  name: string;
  slug: string;
  count: number;
  chassis_model: { [chassisModelLabel: string]: number };
}

interface MakesDateStructure {
  [date: string]: {
    [makeSlug: string]: CachedMakeData;
  };
}

interface AuctionCar {
  _id: string;
  inventory_no: string;
  name: string;
  slug: string;
  company_name: string;
  company_slug: string;
  year: string;
  mileage: number;
  fuel_type: string;
  auction_date: string;
  lot_no: string;
  hall_name: string;
  transmission: string;
  color: string;
  price: string;
  engine: string;
  engine_capacity: number;
  pictures: string[];
  is_sold: boolean;
  type: string;
  created_at: string;
}

@Component({
  selector: 'app-listing-bid',
  templateUrl: './listing-bid.component.html',
  styleUrls: ['./listing-bid.component.scss']
})
export class ListingBidComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];

  // Listing properties
  cars: AuctionCar[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };
  isLoadingCars: boolean = false;
  currentPage: number = 1;

  // Expose Math to template
  Math = Math;

  filter_page: any = null;

  // Filter data
  dateOptions: FilterOption[] = [];
  auctionOptions: FilterOption[] = [];
  makeOptions: FilterOption[] = [];
  modelOptions: FilterOption[] = [];

  // Cached data for smart filtering
  makesDateData: MakesDateStructure = {};
  isLoadingCachedData = false;
  dateHalls: { [key: string]: string[] } = {};
  // More Filters dropdown options
  transmissionOptions: FilterOption[] = [
    { value: 'automatic', label: 'Automatic' },
    { value: 'manual', label: 'Manual' },
    { value: 'cvt', label: 'CVT' }
  ];

  colorOptions: FilterOption[] = [
    { value: 'white', label: 'White' },
    { value: 'black', label: 'Black' },
    { value: 'silver', label: 'Silver' },
    { value: 'red', label: 'Red' },
    { value: 'blue', label: 'Blue' },
    { value: 'gray', label: 'Gray' }
  ];

  displacementOptions: FilterOption[] = [
    { value: '660', label: '660cc' },
    { value: '1000', label: '1000cc' },
    { value: '1500', label: '1500cc' },
    { value: '2000', label: '2000cc' },
    { value: '2500', label: '2500cc' },
    { value: '3000', label: '3000cc' }
  ];

  gradeOptions: FilterOption[] = [
    { value: 'a', label: 'Grade A' },
    { value: 'b', label: 'Grade B' },
    { value: 'c', label: 'Grade C' },
    { value: 'd', label: 'Grade D' }
  ];

  // Year options (from 1990 to current year)
  yearOptions: FilterOption[] = [];

  chassisModelOptions: FilterOption[] = [];

  // Selected filters
  filters: SearchFilters = {
    dates: [],
    auctions: [],
    makes: [],
    models: [],
    chassisModels: [],
    lotNumber: '',
    lotSearchAuction: '',
    // More Filters dropdowns
    yearFrom: '',
    yearTo: '',
    mileageFrom: '',
    mileageTo: '',
    transmission: '',
    color: '',
    displacement: '',
    grade: ''
  };

  // Loading states
  isLoadingMakes = false;
  isLoadingModels = false;
  isLoadingChassisModels = false;

  // Save Search state
  isSaveSearchEnabled = false;

  constructor(
    private _generalService: GeneralService,
    private _carService: CarService,
    private router: Router,
    private route: ActivatedRoute,
    private _savedSearchModalService: SavedSearchModalService,
    private _transmissionService: TransmissionService,
    private _colorService: BodyColorService,
    private _vehicleService: VehicleService
  ) { }

  ngOnInit(): void {
    this.generateDateOptions();
    this.generateYearOptions();
    this.loadCachedMakeData();
    this.loadTransmissions();
    this.loadColors();
    // Check for query parameters from saved search
    this.subscriptions.push(
      this.route.queryParams.subscribe(params => {
        if (Object.keys(params).length > 0) {
          // Apply saved search filters after data is loaded
          setTimeout(() => {
            this.applySavedSearchFilters(params);
          }, 1000); // Wait for data to load
        }
      })
    );
  }

  loadTransmissions(): void {
    this.subscriptions.push(
      this._transmissionService.GetData('1', '', { paginate: false }).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.transmissionOptions = response.data.map((transmission: any) => ({
              value: transmission.slug,
              label: transmission.name
            }));
          }
        },
        error: (error: any) => {
          console.error('Error loading transmissions:', error);
        }
      })
    );
  }

  loadColors(): void {
    this.subscriptions.push(
      this._colorService.GetData('1', '', { paginate: false }).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.colorOptions = response.data.map((transmission: any) => ({
              value: transmission.slug,
              label: transmission.name
            }));
          }
        },
        error: (error: any) => {
          console.error('Error loading transmissions:', error);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  // Populate auction options from date_halls data
  private populateAuctionOptions(): void {
    if (!this.dateHalls || Object.keys(this.dateHalls).length === 0) {
      console.log('No date_halls data available');
      this.auctionOptions = [];
      return;
    }

    // Get all unique halls from date_halls
    const allHalls = new Set<string>();
    Object.keys(this.dateHalls).forEach(date => {
      if (Array.isArray(this.dateHalls[date])) {
        this.dateHalls[date].forEach(hall => allHalls.add(hall));
      }
    });

    // Create auction options
    this.auctionOptions = Array.from(allHalls).map(hall => ({
      value: hall,
      label: hall,
      checked: false
    }));

    // Sort alphabetically
    this.auctionOptions.sort((a, b) => a.label.localeCompare(b.label));

    console.log('Auction options populated:', this.auctionOptions.length);

    // Update halls based on initially selected dates (all dates are checked by default)
    this.updateHallsBasedOnDates();
  }

  // Load cached make data from API (called once)
  private loadCachedMakeData(): void {
    this.isLoadingCachedData = true;
    this.isLoadingMakes = true;

    this.subscriptions.push(
      this._generalService.GetCachedData('alaedo-data').subscribe({
        next: (response: any) => {
          if (response.status && response.data && response.data.make_dates) {
            this.makesDateData = response.data.make_dates;
            console.log('Cached make data loaded:', Object.keys(this.makesDateData).length, 'dates');
            this.populateMakeOptions();
          }
          // Extract date_halls if present
          if (response.status && response.data && response.data.date_halls) {
            this.dateHalls = response.data.date_halls;
            console.log('Date halls loaded:', this.dateHalls);
            // Auto-check halls based on initially selected dates (all dates are checked by default)
            this.populateAuctionOptions();
          }
          this.isLoadingCachedData = false;
          this.isLoadingMakes = false;
        },
        error: (error: any) => {
          console.error('Error loading cached make data:', error);
          this.isLoadingCachedData = false;
          this.isLoadingMakes = false;
        }
      })
    );
  }

  // Populate make options from cached data based on selected dates
  private populateMakeOptions(): void {
    const selectedDates = this.filters.dates;
    if (selectedDates.length === 0) {
      this.makeOptions = [];
      return;
    }

    // Aggregate makes across selected dates
    const makeMap: { [makeSlug: string]: { name: string; count: number } } = {};

    selectedDates.forEach(date => {
      if (this.makesDateData[date]) {
        Object.keys(this.makesDateData[date]).forEach(makeSlug => {
          const make = this.makesDateData[date][makeSlug];
          if (makeMap[makeSlug]) {
            // Make already exists, add to count
            makeMap[makeSlug].count += make.count;
          } else {
            // New make, initialize
            makeMap[makeSlug] = {
              name: make.name,
              count: make.count
            };
          }
        });
      }
    });

    // Create filter options
    this.makeOptions = Object.keys(makeMap).map(makeSlug => ({
      value: makeSlug,
      label: `${makeMap[makeSlug].name} (${makeMap[makeSlug].count})`,
      checked: false
    }));

    // Sort makes by count in descending order
    this.makeOptions.sort((a, b) => {
      // Extract the count from the label (the number inside parentheses)
      const countA = parseInt(a.label.match(/\((\d+)\)$/)?.[1] || '0');
      const countB = parseInt(b.label.match(/\((\d+)\)$/)?.[1] || '0');
      return countB - countA; // Descending order (highest count first)
    });

    console.log('Make options populated:', this.makeOptions.length, 'for dates:', selectedDates);
  }

  // Generate date options for next 7 days
  generateDateOptions(): void {
    const today = new Date();
    this.dateOptions = [];
    this.filters.dates = []; // Initialize dates filter array

    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);

      const dateString = date.toISOString().split('T')[0];
      const label = i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });

      this.dateOptions.push({
        value: dateString,
        label: label,
        checked: true // All dates checked by default
      });

      // Add to filters array since all are checked by default
      this.filters.dates.push(dateString);
    }
    console.log('Date options generated with all checked:', this.filters.dates.length);
  }

  // Generate year options from 1990 to current year
  generateYearOptions(): void {
    const currentYear = new Date().getFullYear();
    for (let year = 1990; year <= currentYear; year++) {
      this.yearOptions.push({
        value: year.toString(),
        label: year.toString()
      });
    }

    this.yearOptions = this.yearOptions.reverse();
  }

  // Populate model options based on selected makes and dates (no API call)
  private populateModelOptions(): void {
    const selectedMakes = this.filters.makes;
    const selectedDates = this.filters.dates;

    if (selectedMakes.length === 0 || selectedDates.length === 0) {
      this.modelOptions = [];
      this.chassisModelOptions = []; // Also clear chassis models when no makes selected
      return;
    }

    this.isLoadingModels = true;

    // Get all models from selected makes across selected dates and aggregate chassis model counts
    const modelCounts: { [key: string]: { name: string; count: number } } = {};

    selectedDates.forEach(date => {
      if (this.makesDateData[date]) {
        selectedMakes.forEach(makeSlug => {
          const make = this.makesDateData[date][makeSlug];
          if (make && make.models) {
            Object.keys(make.models).forEach(modelSlug => {
              const model = make.models[modelSlug];
              if (modelCounts[modelSlug]) {
                // If model already exists, add to count
                modelCounts[modelSlug].count += model.count;
              } else {
                // New model, initialize count
                modelCounts[modelSlug] = {
                  name: model.name,
                  count: model.count
                };
              }
            });
          }
        });
      }
    });

    // Create filter options with aggregated counts
    this.modelOptions = Object.keys(modelCounts).map((modelSlug: string) => ({
      value: modelSlug,
      label: `${modelCounts[modelSlug].name} (${modelCounts[modelSlug].count})`,
      checked: false
    }));

    // Sort models alphabetically by name (case-insensitive)
    this.modelOptions.sort((a, b) => {
      // Extract the name part from the label (before the count)
      const nameA = a.label.replace(/\s*\(\d+\)$/, '').trim().toLowerCase();
      const nameB = b.label.replace(/\s*\(\d+\)$/, '').trim().toLowerCase();
      return nameA.localeCompare(nameB);
    });

    console.log('Model options populated:', this.modelOptions.length, 'for makes:', selectedMakes, 'and dates:', selectedDates);
    this.isLoadingModels = false;
  }

  // Populate chassis model options based on selected models and dates (no API call)
  private populateChassisModelOptions(): void {
    const selectedModels = this.filters.models;
    const selectedDates = this.filters.dates;
    const selectedMakes = this.filters.makes;

    if (selectedModels.length === 0 || selectedDates.length === 0 || selectedMakes.length === 0) {
      this.chassisModelOptions = [];
      return;
    }

    this.isLoadingChassisModels = true;

    // Get all chassis models from selected models across selected dates and aggregate counts
    const chassisModelCounts: { [key: string]: number } = {};

    selectedDates.forEach(date => {
      if (this.makesDateData[date]) {
        selectedMakes.forEach(makeSlug => {
          const make = this.makesDateData[date][makeSlug];
          if (make && make.models) {
            selectedModels.forEach(modelSlug => {
              const model = make.models[modelSlug];
              if (model && model.chassis_model) {
                // chassis_model is now an object with key-value pairs
                // key: chassis model label, value: count
                Object.keys(model.chassis_model).forEach(chassisModelLabel => {
                  const count = model.chassis_model[chassisModelLabel];
                  chassisModelCounts[chassisModelLabel] = (chassisModelCounts[chassisModelLabel] || 0) + count;
                });
              }
            });
          }
        });
      }
    });

    // Create filter options with aggregated counts
    this.chassisModelOptions = Object.keys(chassisModelCounts).map((chassisModel: string) => ({
      value: chassisModel,
      label: `${chassisModel} (${chassisModelCounts[chassisModel]})`,
      checked: false
    }));

    // Sort chassis models by count in descending order
    this.chassisModelOptions.sort((a, b) => {
      // Extract the count from the label (the number inside parentheses)
      const countA = parseInt(a.label.match(/\((\d+)\)$/)?.[1] || '0');
      const countB = parseInt(b.label.match(/\((\d+)\)$/)?.[1] || '0');
      return countB - countA; // Descending order (highest count first)
    });

    console.log('Chassis model options populated:', this.chassisModelOptions.length, 'for models:', selectedModels, 'and dates:', selectedDates);
    this.isLoadingChassisModels = false;
  }

  // Update auction halls based on selected dates
  private updateHallsBasedOnDates(): void {
    if (!this.dateHalls || Object.keys(this.dateHalls).length === 0) {
      console.log('No date_halls data available');
      return;
    }

    // Get all halls that should be checked based on selected dates
    const hallsToCheck = new Set<string>();

    this.filters.dates.forEach(selectedDate => {
      if (this.dateHalls[selectedDate] && Array.isArray(this.dateHalls[selectedDate])) {
        this.dateHalls[selectedDate].forEach(hall => {
          hallsToCheck.add(hall);
        });
      }
    });

    console.log('Halls to check based on selected dates:', Array.from(hallsToCheck));

    // Update auction options - check halls that are in the selected dates, uncheck others
    this.auctionOptions.forEach(option => {
      const shouldBeChecked = hallsToCheck.has(option.value);
      option.checked = shouldBeChecked;

      // Update filters array
      const indexInFilters = this.filters.auctions.indexOf(option.value);
      if (shouldBeChecked && indexInFilters === -1) {
        // Should be checked but not in filters - add it
        this.filters.auctions.push(option.value);
      } else if (!shouldBeChecked && indexInFilters > -1) {
        // Should not be checked but is in filters - remove it
        this.filters.auctions.splice(indexInFilters, 1);
      }
    });

    // Sort auction options: checked ones on top, unchecked ones at bottom
    this.auctionOptions.sort((a, b) => {
      // If both are checked or both are unchecked, maintain alphabetical order
      if (a.checked === b.checked) {
        return a.label.localeCompare(b.label);
      }
      // Checked items come first (return -1 if a is checked and b is not)
      return a.checked ? -1 : 1;
    });

    console.log('Updated auction filters:', this.filters.auctions);
  }

  // Check if all auctions are checked
  isAllAuctionsChecked(): boolean {
    if (this.auctionOptions.length === 0) {
      return false;
    }
    return this.auctionOptions.every(option => option.checked);
  }

  // Handle "All" checkbox change for auctions
  onAuctionAllChange(checked: boolean): void {
    // Update all auction options
    this.auctionOptions.forEach(option => {
      option.checked = checked;
    });

    // Update filters array
    if (checked) {
      // Check all - add all auction values to filters
      this.filters.auctions = this.auctionOptions.map(option => option.value);
    } else {
      // Uncheck all - clear filters
      this.filters.auctions = [];
    }

    console.log('All auctions', checked ? 'checked' : 'unchecked', ':', this.filters.auctions);

    // Update Save Search button state
    this.updateSaveSearchState();
  }

  // Check if all dates are checked
  isAllDatesChecked(): boolean {
    if (this.dateOptions.length === 0) {
      return false;
    }
    return this.dateOptions.every(option => option.checked);
  }

  // Handle "All" checkbox change for dates
  onDateAllChange(checked: boolean): void {
    // Update all date options
    this.dateOptions.forEach(option => {
      option.checked = checked;
    });

    // Update filters array
    if (checked) {
      // Check all - add all date values to filters
      this.filters.dates = this.dateOptions.map(option => option.value);
    } else {
      // Uncheck all - clear filters
      this.filters.dates = [];
    }

    console.log('All dates', checked ? 'checked' : 'unchecked', ':', this.filters.dates);

    // When dates change, refresh makes, models, and chassis models
    this.filters.makes = [];
    this.filters.models = [];
    this.filters.chassisModels = [];
    this.makeOptions.forEach(option => option.checked = false);
    this.modelOptions.forEach(option => option.checked = false);
    this.chassisModelOptions.forEach(option => option.checked = false);
    this.modelOptions = [];
    this.chassisModelOptions = [];
    this.populateMakeOptions();
    this.updateHallsBasedOnDates();

    // Update Save Search button state
    this.updateSaveSearchState();
  }

  // Check if all makes are checked
  isAllMakesChecked(): boolean {
    if (this.makeOptions.length === 0) {
      return false;
    }
    return this.makeOptions.every(option => option.checked);
  }

  // Handle "All" checkbox change for makes
  onMakeAllChange(checked: boolean): void {
    // Update all make options
    this.makeOptions.forEach(option => {
      option.checked = checked;
    });

    // Update filters array
    if (checked) {
      // Check all - add all make values to filters
      this.filters.makes = this.makeOptions.map(option => option.value);
    } else {
      // Uncheck all - clear filters
      this.filters.makes = [];
    }

    // Clear dependent filters (models and chassis models)
    this.filters.models = [];
    this.filters.chassisModels = [];
    this.modelOptions.forEach(option => option.checked = false);
    this.chassisModelOptions.forEach(option => option.checked = false);
    this.chassisModelOptions = [];

    // Populate model options if makes are selected
    if (checked) {
      this.populateModelOptions();
    } else {
      this.modelOptions = [];
    }

    console.log('All makes', checked ? 'checked' : 'unchecked', ':', this.filters.makes);

    // Update Save Search button state
    this.updateSaveSearchState();
  }

  // Check if all models are checked
  isAllModelsChecked(): boolean {
    if (this.modelOptions.length === 0) {
      return false;
    }
    return this.modelOptions.every(option => option.checked);
  }

  // Handle "All" checkbox change for models
  onModelAllChange(checked: boolean): void {
    // Update all model options
    this.modelOptions.forEach(option => {
      option.checked = checked;
    });

    // Update filters array
    if (checked) {
      // Check all - add all model values to filters
      this.filters.models = this.modelOptions.map(option => option.value);
    } else {
      // Uncheck all - clear filters
      this.filters.models = [];
    }

    // Clear dependent filters (chassis models)
    this.filters.chassisModels = [];
    this.chassisModelOptions.forEach(option => option.checked = false);

    // Populate chassis model options if models are selected
    if (checked) {
      this.populateChassisModelOptions();
    } else {
      this.chassisModelOptions = [];
    }

    console.log('All models', checked ? 'checked' : 'unchecked', ':', this.filters.models);

    // Update Save Search button state
    this.updateSaveSearchState();
  }

  // Check if all chassis models are checked
  isAllChassisModelsChecked(): boolean {
    if (this.chassisModelOptions.length === 0) {
      return false;
    }
    return this.chassisModelOptions.every(option => option.checked);
  }

  // Handle "All" checkbox change for chassis models
  onChassisModelAllChange(checked: boolean): void {
    // Update all chassis model options
    this.chassisModelOptions.forEach(option => {
      option.checked = checked;
    });

    // Update filters array
    if (checked) {
      // Check all - add all chassis model values to filters
      this.filters.chassisModels = this.chassisModelOptions.map(option => option.value);
    } else {
      // Uncheck all - clear filters
      this.filters.chassisModels = [];
    }

    console.log('All chassis models', checked ? 'checked' : 'unchecked', ':', this.filters.chassisModels);

    // Update Save Search button state
    this.updateSaveSearchState();
  }

  // Handle checkbox changes for different filter types
  onFilterChange(filterType: keyof SearchFilters, value: string, checked: boolean): void {
    if (filterType === 'lotNumber') {
      return; // Lot number is handled separately
    }

    const filterArray = this.filters[filterType] as string[];

    if (checked) {
      if (!filterArray.includes(value)) {
        filterArray.push(value);
      }
    } else {
      const index = filterArray.indexOf(value);
      if (index > -1) {
        filterArray.splice(index, 1);
      }
    }

    // Update the corresponding option's checked state
    this.updateOptionCheckedState(filterType, value, checked);

    // Handle cascading filter updates
    if (filterType === 'dates') {
      // When dates change, refresh makes, models, and chassis models
      this.filters.makes = []; // Clear selected makes
      this.filters.models = []; // Clear selected models
      this.filters.chassisModels = []; // Clear selected chassis models
      this.makeOptions.forEach(option => option.checked = false); // Clear make checkboxes
      this.modelOptions.forEach(option => option.checked = false); // Clear model checkboxes
      this.chassisModelOptions.forEach(option => option.checked = false); // Clear chassis model checkboxes
      this.modelOptions = []; // Clear model options
      this.chassisModelOptions = []; // Clear chassis model options
      this.populateMakeOptions(); // Repopulate makes based on new dates
      this.updateHallsBasedOnDates(); // Update halls based on new dates
    }

    if (filterType === 'makes') {
      this.filters.models = []; // Clear selected models
      this.filters.chassisModels = []; // Clear selected chassis models
      this.modelOptions.forEach(option => option.checked = false); // Clear model checkboxes
      this.chassisModelOptions.forEach(option => option.checked = false); // Clear chassis model checkboxes
      this.chassisModelOptions = []; // Clear chassis model options completely
      this.populateModelOptions();
    }

    if (filterType === 'models') {
      this.filters.chassisModels = []; // Clear selected chassis models
      this.chassisModelOptions.forEach(option => option.checked = false); // Clear chassis model checkboxes
      this.populateChassisModelOptions();
    }

    console.log(`${filterType} filters:`, this.filters[filterType]);

    // Update Save Search button state
    this.updateSaveSearchState();
  }

  // Update option checked state
  updateOptionCheckedState(filterType: keyof SearchFilters, value: string, checked: boolean): void {
    let options: FilterOption[] = [];

    switch (filterType) {
      case 'dates':
        options = this.dateOptions;
        break;
      case 'auctions':
        options = this.auctionOptions;
        break;
      case 'makes':
        options = this.makeOptions;
        break;
      case 'models':
        options = this.modelOptions;
        break;
      case 'chassisModels':
        options = this.chassisModelOptions;
        break;
    }

    const option = options.find(opt => opt.value === value);
    if (option) {
      option.checked = checked;
    }
  }

  // Handle dropdown changes for more filters
  onDropdownChange(filterType: keyof SearchFilters, value: string): void {
    (this.filters as any)[filterType] = value;
    console.log(`${filterType} filter:`, value);

    // Update Save Search button state
    this.updateSaveSearchState();
  }

  // Handle mileage input - only allow numbers
  onMileageInput(filterType: 'mileageFrom' | 'mileageTo', value: string): void {
    // Remove any non-numeric characters
    const numericValue = value.replace(/[^0-9]/g, '');
    this.filters[filterType] = numericValue;
    console.log(`${filterType} filter:`, numericValue);

    // Update Save Search button state
    this.updateSaveSearchState();
  }

  // Handle lot number input
  onLotNumberChange(value: string): void {
    this.filters.lotNumber = value;
    console.log('Lot number:', this.filters.lotNumber);
  }

  // Search by lot number
  searchByLotNumber(): void {
    if (!this.filters.lotNumber.trim()) {
      console.warn('Please enter a lot number');
      return;
    }

    const lotNumbers = this.filters.lotNumber.split(',').map(num => num.trim()).filter(num => num);
    const lotFilters: Record<string, any> = {
      lot_numbers: lotNumbers.join(',')
    };

    if (this.filters.lotSearchAuction) {
      lotFilters['auction'] = this.filters.lotSearchAuction;
    }

    this.filter_page = lotFilters;

    this.loadCars(1, '', lotFilters);
  }

  // Apply main filters (excludes lot number search)
  applyMainFilters(): void {
    const mainFilters: Record<string, any> = {};

    // Add non-empty filters
    if (this.filters.dates.length > 0) {
      mainFilters['auction_dates'] = this.filters.dates.join(',');
    }
    if (this.filters.auctions.length > 0) {
      mainFilters['auctions'] = this.filters.auctions.join(',');
    }
    if (this.filters.makes.length > 0) {
      mainFilters['makes'] = this.filters.makes.join(',');
    }
    if (this.filters.models.length > 0) {
      mainFilters['models'] = this.filters.models.join(',');
    }
    if (this.filters.chassisModels.length > 0) {
      mainFilters['chassis_models'] = this.filters.chassisModels.join(',');
    }

    // More Filters
    if (this.filters.yearFrom) {
      mainFilters['year_from'] = this.filters.yearFrom;
    }
    if (this.filters.yearTo) {
      mainFilters['year_to'] = this.filters.yearTo;
    }
    if (this.filters.mileageFrom) {
      mainFilters['mileage_from'] = this.filters.mileageFrom;
    }
    if (this.filters.mileageTo) {
      mainFilters['mileage_to'] = this.filters.mileageTo;
    }
    if (this.filters.transmission) {
      mainFilters['transmission_slug'] = this.filters.transmission;
    }
    if (this.filters.color) {
      mainFilters['color_slug'] = this.filters.color;
    }
    if (this.filters.displacement) {
      mainFilters['displacement'] = this.filters.displacement;
    }
    if (this.filters.grade) {
      mainFilters['grade'] = this.filters.grade;
    }

    console.log('Applying main filters:', mainFilters);

    this.filter_page = mainFilters;

    this.loadCars(1, '', mainFilters);
  }

  // Apply filters (for backward compatibility)
  applyFilters(): void {
    this.applyMainFilters();
  }

  // Clear all filters
  clearFilters(): void {
    this.filters = {
      dates: [],
      auctions: [],
      makes: [],
      models: [],
      chassisModels: [],
      lotNumber: '',
      lotSearchAuction: '',
      // More Filters dropdowns
      yearFrom: '',
      yearTo: '',
      mileageFrom: '',
      mileageTo: '',
      transmission: '',
      color: '',
      displacement: '',
      grade: ''
    };

    // Reset all checkbox states
    this.dateOptions.forEach(option => option.checked = false);
    this.auctionOptions.forEach(option => option.checked = false);
    this.makeOptions.forEach(option => option.checked = false);
    this.modelOptions.forEach(option => option.checked = false);
    this.chassisModelOptions.forEach(option => option.checked = false);

    console.log('Filters cleared');

    // Update Save Search button state
    this.updateSaveSearchState();
  }

  // Check if Save Search button should be enabled
  // Enabled when any filter other than Date is selected
  updateSaveSearchState(): void {
    this.isSaveSearchEnabled =
      this.filters.auctions.length > 0 ||
      this.filters.makes.length > 0 ||
      this.filters.models.length > 0 ||
      this.filters.chassisModels.length > 0 ||
      !!this.filters.yearFrom ||
      !!this.filters.yearTo ||
      !!this.filters.mileageFrom ||
      !!this.filters.mileageTo ||
      !!this.filters.transmission ||
      !!this.filters.color ||
      !!this.filters.displacement ||
      !!this.filters.grade;
  }

  // Save search - opens modal to get search name
  saveSearch(): void {
    if (!this.isSaveSearchEnabled) {
      console.warn('No filters selected to save');
      return;
    }

    // Get label names for selected values
    const getLabels = (values: string[], options: FilterOption[]): string => {
      return values
        .map(value => {
          const option = options.find(opt => opt.value === value);
          return option ? option.value.replace(/\s*\(\d+\)/, '') : value; // Remove count from label
        })
        .join(',');
    };

    // Get label for a single dropdown value
    const getLabel = (value: string, options: FilterOption[]): string => {
      const option = options.find(opt => opt.value === value);
      return option ? option.label : value;
    };

    const saveData: any = {};

    if (this.filters.auctions.length > 0) {
      saveData['Auction'] = getLabels(this.filters.auctions, this.auctionOptions);
    }
    if (this.filters.makes.length > 0) {
      saveData['Make'] = getLabels(this.filters.makes, this.makeOptions);
    }
    if (this.filters.models.length > 0) {
      saveData['Model'] = getLabels(this.filters.models, this.modelOptions);
    }
    if (this.filters.chassisModels.length > 0) {
      saveData['Chassis Model'] = getLabels(this.filters.chassisModels, this.chassisModelOptions);
    }

    // Add year range filters
    if (this.filters.yearFrom) {
      saveData['Year From'] = this.filters.yearFrom;
    }
    if (this.filters.yearTo) {
      saveData['Year To'] = this.filters.yearTo;
    }

    // Add mileage range filters
    if (this.filters.mileageFrom) {
      saveData['Mileage From'] = this.filters.mileageFrom + ' km';
    }
    if (this.filters.mileageTo) {
      saveData['Mileage To'] = this.filters.mileageTo + ' km';
    }

    // Add transmission filter
    if (this.filters.transmission) {
      saveData['Transmission'] = getLabel(this.filters.transmission, this.transmissionOptions);
    }

    // Add color filter
    if (this.filters.color) {
      saveData['Color'] = getLabel(this.filters.color, this.colorOptions);
    }

    // Add displacement filter
    if (this.filters.displacement) {
      saveData['Displacement'] = getLabel(this.filters.displacement, this.displacementOptions);
    }

    // Add grade filter
    if (this.filters.grade) {
      saveData['Grade'] = getLabel(this.filters.grade, this.gradeOptions);
    }

    console.log('=== SAVE SEARCH DATA ===');
    console.log(saveData);
    console.log('========================');

    // Set the search data in the modal service
    this._savedSearchModalService.setSearchData(saveData);

    // Open the modal
    const modalElement = document.getElementById('kt_modal_add_saved_search');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  // Handle search saved event from modal
  onSearchSaved(): void {
    console.log('Search saved successfully');
    // Optionally, you can show a success message or refresh data here
  }

  // Load cars from API
  loadCars(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoadingCars = true;
    this.currentPage = page;

    filters.type = 'alaedo';
    this.subscriptions.push(
      this._carService.GetDataPost(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<AuctionCar>) => {
          if (response.status && response.data) {
            this.cars = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.cars = [];
            this.resetPagination();
          }
          this.isLoadingCars = false;
        },
        error: (error) => {
          console.error('Error loading cars:', error);
          this.cars = [];
          this.resetPagination();
          this.isLoadingCars = false;
        }
      })
    );
  }

  // Reset pagination
  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Handle page change
  onPageChange(page: number): void {
    // Determine which filters to use based on current search type
    // This is a simplified approach - you might want to track search type
    this.loadCars(page, '', this.filter_page || {});
  }

  // Format price
  formatPrice(price: string | number): string {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (!price || isNaN(numPrice)) return '¥0';
    return '¥' + numPrice.toLocaleString();
  }

  // Format mileage
  formatMileage(mileage: number): string {
    if (!mileage) return '0 km';
    return mileage.toLocaleString() + ' km';
  }

  // Get main image
  getMainImage(car: AuctionCar): string {
    let pictures = car?.pictures?.length
      ? [...car.pictures.slice(1), car.pictures[0]]
      : [];
    if (pictures.length > 0) {
      return pictures[0];
    }
    return 'assets/media/stock/600x400/img-1.jpg';
  }

  // Format auction date
  formatAuctionDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric'
    });
  }

  // Track by function for ngFor
  trackByFn(_index: number, car: AuctionCar): string {
    return car._id;
  }

  // Navigate to view bid page
  onRowClick(car: AuctionCar): void {
    window.open('/bid-manager/search-bids/view-bid/' + car._id, '_blank');
    // this.router.navigate(['auction/search-bids/view-bid', car._id]);
  }

  // Format label with count styling
  formatLabelWithCount(label: string, show_count=true): string {
    // Extract name and count from label like "TOYOTA (35)"
    const match = label.match(/^(.+?)\s*\((\d+)\)$/);
    if (match) {
      const name = match[1];
      const count = match[2];
      return show_count ? `${name} <span class="badge badge-light-primary ms-2">${count}</span>` : name;
      // return `${name} <span class="badge badge-light-primary ms-2">${count}</span>`;

    }
    return label;
  }

  // Apply saved search filters from query parameters
  private applySavedSearchFilters(params: any): void {
    console.log('Applying saved search filters:', params);

    // Map of saved search keys to filter types
    const filterMapping: Record<string, keyof SearchFilters> = {
      'Auction': 'auctions',
      'Make': 'makes',
      'Model': 'models',
      'Chassis Model': 'chassisModels'
    };

    // Process each query parameter
    Object.keys(params).forEach(key => {
      const filterType = filterMapping[key];
      if (filterType && params[key]) {
        // Split comma-separated values
        const values = params[key].split(',').map((v: string) => v.trim().toLowerCase());

        // Apply filters based on type
        if (filterType === 'auctions') {
          this.applyAuctionFilters(values);
        } else if (filterType === 'makes') {
          this.applyMakeFilters(values);
        } else if (filterType === 'models') {
          this.applyModelFilters(values);
        } else if (filterType === 'chassisModels') {
          this.applyChassisModelFilters(values);
        }
      }
    });

    // Apply dropdown filters (year, mileage, transmission, color, displacement, grade)
    if (params['Year From']) {
      this.filters.yearFrom = params['Year From'];
    }
    if (params['Year To']) {
      this.filters.yearTo = params['Year To'];
    }
    if (params['Mileage From']) {
      // Extract numeric value from the label (e.g., "10000 km" -> "10000")
      this.filters.mileageFrom = params['Mileage From'].replace(/[^0-9]/g, '');
    }
    if (params['Mileage To']) {
      // Extract numeric value from the label (e.g., "50000 km" -> "50000")
      this.filters.mileageTo = params['Mileage To'].replace(/[^0-9]/g, '');
    }
    if (params['Transmission']) {
      // Find the value from the label
      const transmissionOption = this.transmissionOptions.find(opt => opt.label === params['Transmission']);
      this.filters.transmission = transmissionOption ? transmissionOption.value : params['Transmission'];
    }
    if (params['Color']) {
      // Find the value from the label
      const colorOption = this.colorOptions.find(opt => opt.label === params['Color']);
      this.filters.color = colorOption ? colorOption.value : params['Color'];
    }
    if (params['Displacement']) {
      // Find the value from the label
      const displacementOption = this.displacementOptions.find(opt => opt.label === params['Displacement']);
      this.filters.displacement = displacementOption ? displacementOption.value : params['Displacement'];
    }
    if (params['Grade']) {
      // Find the value from the label
      const gradeOption = this.gradeOptions.find(opt => opt.label === params['Grade']);
      this.filters.grade = gradeOption ? gradeOption.value : params['Grade'];
    }

    // Update Save Search button state
    this.updateSaveSearchState();

    // After applying all filters, trigger the search
    setTimeout(() => {
      this.applyMainFilters();
    }, 500);
  }

  private applyAuctionFilters(values: string[]): void {
    this.auctionOptions.forEach(option => {
      if (values.includes(option.value.toLowerCase())) {
        option.checked = true;
        if (!this.filters.auctions.includes(option.value)) {
          this.filters.auctions.push(option.value);
        }
      }
    });
  }

  private applyMakeFilters(values: string[]): void {
    this.makeOptions.forEach(option => {
      const optionValue = option.value.replace(/\s*\(\d+\)/, '').toLowerCase();
      if (values.includes(optionValue)) {
        option.checked = true;
        if (!this.filters.makes.includes(option.value)) {
          this.filters.makes.push(option.value);
        }
      }
    });

    // Populate model options after makes are selected
    if (this.filters.makes.length > 0) {
      this.populateModelOptions();
    }
  }

  private applyModelFilters(values: string[]): void {
    // Wait a bit for model options to be populated
    setTimeout(() => {
      this.modelOptions.forEach(option => {
        const optionValue = option.value.replace(/\s*\(\d+\)/, '').toLowerCase();
        if (values.includes(optionValue)) {
          option.checked = true;
          if (!this.filters.models.includes(option.value)) {
            this.filters.models.push(option.value);
          }
        }
      });

      // Populate chassis model options after models are selected
      if (this.filters.models.length > 0) {
        this.populateChassisModelOptions();
      }
    }, 300);
  }

  private applyChassisModelFilters(values: string[]): void {
    // Wait a bit for chassis model options to be populated
    setTimeout(() => {
      this.chassisModelOptions.forEach(option => {
        // Extract the chassis model name from the label (before the count)
        const optionValue = option.value.replace(/\s*\(\d+\)/, '').toLowerCase();
        if (values.includes(optionValue)) {
          option.checked = true;
          if (!this.filters.chassisModels.includes(option.value)) {
            this.filters.chassisModels.push(option.value);
          }
        }
      });
    }, 600);
  }

  // Vehicle actions - Save Vehicle
  onSaveVehicle(event: Event, car: AuctionCar): void {
    // Stop event propagation to prevent row click
    event.stopPropagation();

    console.log('Save vehicle:', car);
    Swal.fire({
      title: 'Save Vehicle',
      text: `Do you want to save ${car.name}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Save',
      cancelButtonText: 'Cancel'
    }).then((result: any) => {
      if (result.isConfirmed) {
        // Implement save logic here
        this.performAddToStock(car);
      }
    });
  }

  private performAddToStock(car: any): void {
    const vehicleId = car._id;

    this.subscriptions.push(
      this._vehicleService.AddToStock(vehicleId).subscribe({
        next: (response: any) => {
          if (response.status) {
            // Show success message
            Swal.fire({
              title: 'Success!',
              text: 'Vehicle has been successfully added to real stock.',
              icon: 'success',
              confirmButtonText: 'OK',
              buttonsStyling: false,
              customClass: {
                confirmButton: 'btn btn-primary'
              }
            });

            // Reload the listing to reflect changes
            this.loadCars(this.currentPage, '', this.filter_page || {});
          } else {
            // Show error message
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to add vehicle to real stock.',
              icon: 'error',
              confirmButtonText: 'OK',
              buttonsStyling: false,
              customClass: {
                confirmButton: 'btn btn-primary'
              }
            });
          }
        },
        error: (error) => {
          console.error('Error adding vehicle to stock:', error);

          // Show error message
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while adding vehicle to real stock.',
            icon: 'error',
            confirmButtonText: 'OK',
            buttonsStyling: false,
            customClass: {
              confirmButton: 'btn btn-primary'
            }
          });
        }
      })
    );
  }

  // Vehicle actions - Make Bid
  onMakeBid(event: Event, car: AuctionCar): void {
    // Stop event propagation to prevent row click
    event.stopPropagation();

    console.log('Make bid for vehicle:', car);
    window.open('/bid-manager/search-bids/view-bid/' + car._id, '_blank');
  }
}
